/**
 * Habitia — Registro central de procesos operacionales (Fase 2).
 *
 * Reúne todas las `ProcessDefinition` (una por archivo en ./definitions) y expone
 * helpers de consulta para la UI del apartado "Procesos" (Super Admin).
 * Es documentación: no toca Firestore ni ejecuta lógica de negocio.
 */

import { CATEGORIES, CATEGORY_META, STEP_TYPES } from "./schema";

import cierreMes from "./definitions/cierreMes";
import cierreAprobacion from "./definitions/cierreAprobacion";
import cobranza from "./definitions/cobranza";
import pagos from "./definitions/pagos";
import fondos from "./definitions/fondos";
import multas from "./definitions/multas";
import presupuestos from "./definitions/presupuestos";
import conciliacion from "./definitions/conciliacion";
import informeGc from "./definitions/informeGc";
import altaEdificio from "./definitions/altaEdificio";
import activacionCuenta from "./definitions/activacionCuenta";
import gestionUsuarios from "./definitions/gestionUsuarios";
import permisos from "./definitions/permisos";
import proracionOcupante from "./definitions/proracionOcupante";
import gestionUnidades from "./definitions/gestionUnidades";
import empleados from "./definitions/empleados";
import empleadosTareas from "./definitions/empleadosTareas";
import empleadosSolicitudes from "./definitions/empleadosSolicitudes";
import empleadosChat from "./definitions/empleadosChat";
import empleadosRecibos from "./definitions/empleadosRecibos";
import eliminacionEdificio from "./definitions/eliminacionEdificio";
import saasLeads from "./definitions/saasLeads";
import perfilConsentimiento from "./definitions/perfilConsentimiento";
import incidencias from "./definitions/incidencias";
import proveedores from "./definitions/proveedores";
import amenities from "./definitions/amenities";
import asambleas from "./definitions/asambleas";
import whatsappHabi from "./definitions/whatsappHabi";
import facturasIa from "./definitions/facturasIa";
import revisiones from "./definitions/revisiones";
import obras from "./definitions/obras";
import novedades from "./definitions/novedades";
import documentacion from "./definitions/documentacion";
import centroComando from "./definitions/centroComando";
import mora from "./definitions/mora";

/** @type {import('./schema').ProcessDefinition[]} */
export const PROCESSES = [
  cierreMes,
  cierreAprobacion,
  cobranza,
  pagos,
  fondos,
  multas,
  mora,
  presupuestos,
  conciliacion,
  informeGc,
  altaEdificio,
  activacionCuenta,
  gestionUsuarios,
  permisos,
  proracionOcupante,
  gestionUnidades,
  empleados,
  empleadosTareas,
  empleadosSolicitudes,
  empleadosChat,
  empleadosRecibos,
  eliminacionEdificio,
  saasLeads,
  perfilConsentimiento,
  incidencias,
  proveedores,
  amenities,
  asambleas,
  whatsappHabi,
  facturasIa,
  revisiones,
  obras,
  novedades,
  documentacion,
  centroComando,
];

/** @returns {import('./schema').ProcessDefinition[]} */
export function getAllProcesses() {
  return PROCESSES;
}

/**
 * @param {string} id
 * @returns {import('./schema').ProcessDefinition | null}
 */
export function getProcessById(id) {
  return PROCESSES.find((p) => p.id === id) || null;
}

/**
 * Agrupa los procesos por categoría, en el orden de CATEGORIES. Omite vacías.
 * @returns {{ category: string, meta: { label: string, accent: string }, processes: import('./schema').ProcessDefinition[] }[]}
 */
export function getProcessesByCategory() {
  return CATEGORIES.map((category) => ({
    category,
    meta: CATEGORY_META[category],
    processes: PROCESSES.filter((p) => p.category === category),
  })).filter((group) => group.processes.length > 0);
}

/**
 * Valida la integridad del registro (referencias de pasos/carriles, tipos, etc.).
 * @param {import('./schema').ProcessDefinition[]} [list]
 * @returns {string[]} lista de errores (vacía = OK)
 */
export function validateRegistry(list = PROCESSES) {
  const errors = [];
  const seenIds = new Set();
  const validTypes = new Set(STEP_TYPES);
  const validStatus = new Set(["COMPLETO", "PARCIAL", "INFERIDO"]);
  const validCats = new Set(CATEGORIES);

  for (const p of list) {
    const tag = p && p.id ? p.id : "(sin id)";
    if (!p || !p.id) {
      errors.push("Proceso sin id");
      continue;
    }
    if (seenIds.has(p.id)) errors.push(`${tag}: id duplicado`);
    seenIds.add(p.id);
    if (!p.name) errors.push(`${tag}: sin name`);
    if (!validCats.has(p.category)) errors.push(`${tag}: categoría inválida "${p.category}"`);
    if (!validStatus.has(p.status)) errors.push(`${tag}: status inválido "${p.status}"`);
    if (!Array.isArray(p.lanes) || p.lanes.length === 0) errors.push(`${tag}: sin lanes`);
    if (!Array.isArray(p.steps) || p.steps.length === 0) errors.push(`${tag}: sin steps`);

    const laneIds = new Set((p.lanes || []).map((l) => l.id));
    const stepIds = new Set();
    let hasStart = false;
    let hasEnd = false;

    for (const s of p.steps || []) {
      if (!s.id) {
        errors.push(`${tag}: step sin id`);
        continue;
      }
      if (stepIds.has(s.id)) errors.push(`${tag}: step id duplicado "${s.id}"`);
      stepIds.add(s.id);
      if (!validTypes.has(s.type)) errors.push(`${tag}: step "${s.id}" tipo inválido "${s.type}"`);
      if (!laneIds.has(s.laneId)) errors.push(`${tag}: step "${s.id}" laneId inexistente "${s.laneId}"`);
      if (s.type === "start") hasStart = true;
      if (s.type === "end") hasEnd = true;
    }
    if (!hasStart) errors.push(`${tag}: sin paso start`);
    if (!hasEnd) errors.push(`${tag}: sin paso end`);

    for (const t of p.transitions || []) {
      if (!stepIds.has(t.from)) errors.push(`${tag}: transición desde step inexistente "${t.from}"`);
      if (!stepIds.has(t.to)) errors.push(`${tag}: transición hacia step inexistente "${t.to}"`);
    }
  }
  return errors;
}
